import { useState, type ReactNode, type RefObject } from 'react';
import { Button } from '@voll-intelligence/ui-kit/ui/button';
import { BottomSheet } from '../bottom_sheet.js';

/**
 * Os filtros de uma página de resultados no palco ESTREITO (#2663): a folha
 * inferior em tela cheia, com o painel de cortes dentro — o X do cabeçalho do
 * painel fecha, o botão do pé comete.
 *
 * O corte é um RASCUNHO enquanto a folha está aberta: a coluna do desktop
 * aplica cada toque na hora porque a lista está ao lado, reagindo; aqui a
 * folha COBRE a lista, e um botão "Aplicar" que só fechasse mentiria sobre o
 * que fez. Então o rascunho nasce do corte em cena a cada abertura, as
 * contagens do painel respondem ao rascunho (são da amostra carregada, como
 * sempre), e só o Aplicar entrega o valor ao dono — fechar pelo X, pelo véu
 * ou por Escape descarta. O `Redefinir` do painel zera o rascunho, não o corte
 * aplicado. O painel em si é do produto (`children`), montado sobre o rascunho.
 *
 * O corte aplicado pode mudar COM a folha aberta: o composer do pé aplica na
 * hora. O rascunho acompanha — ver `rebaseDraft`.
 */
export function ResultsFiltersSheet<Value extends object>({
  open,
  onOpenChange,
  returnFocusTo,
  value,
  label,
  applyLabel,
  locked = false,
  onApply,
  footer,
  children,
}: {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  /** O botão `Filtros` que abriu a folha — recebe o foco de volta ao fechar. */
  readonly returnFocusTo: RefObject<HTMLElement | null>;
  /** O corte APLICADO — a semente do rascunho a cada abertura. */
  readonly value: Value;
  /** O nome acessível da folha ("Filtros"). */
  readonly label: string;
  /** O botão do pé: comete o rascunho e fecha. */
  readonly applyLabel: string;
  readonly locked?: boolean;
  /** O rascunho cometido — a única escrita que sai daqui. */
  readonly onApply: (value: Value) => void;
  /** No pé fixo, acima do Aplicar: o composer do filtro por texto (ele aplica na
   *  hora; o Aplicar comete o rascunho dos eixos). */
  readonly footer?: ReactNode;
  /** O painel do produto sobre o rascunho: `dismiss='close'` e `frame={false}`
   *  são dele; `onCollapse` deve chamar `close`. Quem NÃO monta o
   *  `ResultsFiltersPanel` traz a própria casca de rolagem (`min-height: 0;
   *  flex: 1; overflow-y: auto`): a folha só dá altura à casca compartilhada, e
   *  um filho cru cresce além dela, é clipado pelo Drawer e empurra o Aplicar
   *  para fora da tela (#3299). */
  readonly children: (slot: {
    readonly draft: Value;
    readonly setDraft: (value: Value) => void;
    readonly close: () => void;
  }) => ReactNode;
}): ReactNode {
  const [draft, setDraft] = useState(value);
  /* Re-semeia no RENDER em que a folha abre (o mesmo padrão do `resetToken`
     do carregar-mais do mapa): a folha fica montada fechada para a saída
     animar, então o rascunho da abertura anterior ainda está aqui — e um
     corte descartado não pode voltar como se fosse o corrente. */
  const [seenOpen, setSeenOpen] = useState(open);
  /** O corte aplicado de onde o rascunho saiu (ou para onde foi rebaseado). */
  const [base, setBase] = useState(value);
  if (seenOpen !== open) {
    setSeenOpen(open);
    if (open) {
      setDraft(value);
      setBase(value);
    }
  } else if (open && value !== base) {
    setDraft(rebaseDraft(base, draft, value));
    setBase(value);
  }
  const close = () => {
    onOpenChange(false);
  };
  return (
    <BottomSheet
      open={open}
      onOpenChange={onOpenChange}
      returnFocusTo={returnFocusTo}
      label={label}
      fullscreen
    >
      <div className="iuk-results-filters-sheet">
        {children({ draft, setDraft, close })}
        <div className="iuk-results-filters-sheet-footer">
          {footer}
          <Button
            type="button"
            size="xl"
            className="w-full rounded-full"
            disabled={locked}
            onClick={() => {
              onApply(draft);
              close();
            }}
          >
            {applyLabel}
          </Button>
        </div>
      </div>
    </BottomSheet>
  );
}

/**
 * O rascunho sobre um corte aplicado que mudou por FORA da folha (o composer do
 * pé cria o chip direto no corte em cena): os campos que mudaram lá entram, os
 * demais ficam como a pessoa deixou. Sem isso o Aplicar devolvia o rascunho da
 * abertura e desfazia o chip que tinha acabado de nascer (#3844). Conflito — o
 * mesmo campo mexido nos dois — fica com o corte aplicado: é o gesto mais novo.
 */
function rebaseDraft<Value extends object>(base: Value, draft: Value, next: Value): Value {
  const out = { ...draft };
  for (const key of Object.keys(next) as (keyof Value)[]) {
    if (!sameField(next[key], base[key])) out[key] = next[key];
  }
  return out;
}

/** Lista compara por conteúdo: quem recria o corte inteiro (o primeiro quadro da
 *  lista no explorer de voo) entrega listas novas com os mesmos itens, e isso não
 *  é mudança — senão apagava a seleção pendente do rascunho. */
function sameField(a: unknown, b: unknown): boolean {
  if (!Array.isArray(a) || !Array.isArray(b)) return Object.is(a, b);
  return a.length === b.length && a.every((item, index) => Object.is(item, b[index]));
}
